'use client';

import { Close, Search } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, useId, useRef } from 'react';

type SearchInputProps = Omit<React.ComponentProps<'div'>, 'onChange' | 'onFocus' | 'onBlur'> & {
  placeholder: string;
  onFocus?: () => void;
  onBlur?: () => void;
  value: string;
  onChange?: (value: string) => void;
  onCancel?: () => void;
  showCancel?: boolean;
  'data-testid'?: string;
};

function SearchInput({
  placeholder,
  onFocus,
  onBlur,
  value,
  onChange,
  onCancel,
  showCancel = false,
  className,
  'data-testid': dataTestId = 'search-input',
  ...props
}: SearchInputProps) {
  const t = useTranslations('general.search');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const handleFocus = () => {
    onFocus?.();
  };

  const handleBlur = () => {
    onBlur?.();
  };

  const handleClear = () => {
    onChange?.('');
    inputRef.current?.focus();
  };

  const handleCancel = () => {
    onChange?.('');
    inputRef.current?.blur();
    onCancel?.();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  return (
    <div className={twMerge('flex items-center w-full', className)} data-track-name="Search" data-track-container="Product Search" {...props}>
      <div className="bg-container flex gap-3 items-center flex-1 min-w-0 px-3 py-2.5 rounded-full">
        <label htmlFor={inputId} className="sr-only">
          {placeholder}
        </label>
        <Search size="medium" className="text-foreground" data-testid="search-icon" />
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="bg-transparent text-foreground flex-1 min-w-0 focus:outline-none placeholder:text-foreground-subtle"
          data-testid={dataTestId}
        />
        {value && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={handleClear}
            className="bg-button-background-inverse-hover flex items-center justify-center p-1 rounded-full shrink-0"
            aria-label={t('clear_search')}
            data-track-name="Close"
            data-testid="clear-button"
          >
            <Close size="extra-small" data-testid="close-icon" />
          </button>
        )}
      </div>
      <div
        aria-hidden={!showCancel}
        className={`transition-all duration-300 ease-in-out overflow-hidden ${showCancel ? 'w-16 opacity-100' : 'w-0 opacity-0'}`}
        data-track-container="Product Search"
        data-testid="cancel-wrapper"
      >
        <button
          type="button"
          onClick={handleCancel}
          tabIndex={showCancel ? 0 : -1}
          className="ml-2 paragraph-l-strong text-foreground whitespace-nowrap"
          aria-label={t('cancel')}
          data-track-name="Cancel"
          data-testid="cancel-button"
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );
}

export { SearchInput };
